@if (label) {
  <app-control-header
    [label]="label"
    [required]="required"
    [disabled]="isReadOnly"
  ></app-control-header>
}

<div #trigger class="flex flex-row" cdkOverlayOrigin>
  @if (customButton) {
    <div (click)="!isReadOnly && toggleDropdown($event)">
      <ng-container *ngTemplateOutlet="customButton"></ng-container>
    </div>
  } @else {
    @if (!isMixed) {
      <app-colored-dropdown-menu-button
        [buttonBackgroundClass]="buttonBackgroundClass"
        [textClasses]="textClasses"
        [radius]="radius"
        [isReadOnly]="isReadOnly"
        [tooltip]="hasTooltip ? (tooltip ?? (value ? defaultTooltip : null)) : null"
        [tooltipPlacement]="tooltipPlacement"
        [keepTooltipOnOver]="!value"
        [verticalSize]="verticalSize"
        [horizontalSize]="'small'"
        [arrowEnabled]="arrowEnabled"
        [arrowBlackColor]="arrowBlackColor"
        [isOpen]="isDropdownOpened"
        [buttonIcon]="buttonIcon"
        [iconClasses]="iconClasses"
        (click)="!isReadOnly && toggleDropdown($event)"
      >
        @if (value) {
          @if (!valueTemplate) {
            {{ getDisplayValue(value) | translate }}
          } @else {
            <ng-container
              *ngTemplateOutlet="valueTemplate; context: { displayValue: getDisplayValue(value), selectedValue: value }"
            ></ng-container>
          }
        } @else {
          {{ noValueTextTranslationKey || 'components.dropdowns.coloredDropdownControl.notSet' | translate }}
        }
      </app-colored-dropdown-menu-button>
    } @else {
      <app-new-button
        class="relative"
        [size]="'responsive'"
        [type]="'basic'"
        (click)="!isReadOnly && toggleDropdown($event)"
      >
        @if (mixedButtonIcon) {
          <svg-icon class="mr-1.5" [src]="mixedButtonIcon" />
        }
        {{ mixedButtonTranslationKey | translate }}
        @if (mixedLabelTranslationKey) {
          <span
            class="absolute flex justify-center items-center bottom-px right-px font-main text-xxs h-3 w-6 text-white bg-pink-60 px-0.5 rounded-13"
          >
            {{ mixedLabelTranslationKey | translate }}
          </span>
        }
      </app-new-button>
    }
  }
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="trigger"
  [cdkConnectedOverlayOpen]="isDropdownOpened"
  [cdkConnectedOverlayPanelClass]="overlayPanelClass"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="'cdk-overlay-transparent-backdrop'"
  (backdropClick)="close()"
>
  <app-dropdown-options-backdrop
    #openableElement
    data-test-id="colored-dropdown"
    [class]="dropdownClasses"
    [style.width.px]="useInlineWidth ? listWidth : null"
  >
    @if (searchEnabled) {
      <app-dropdown-search-input
        class="w-full"
        [formControl]="searchField"
        [placeholder]="searchFieldPlaceholder | translate"
        (input)="handleSearchInput()"
        (click)="searchFieldClick($event)"
      ></app-dropdown-search-input>
    }

    <perfect-scrollbar [style.max-height]="listMaxHeight + 'px'" [config]="{ suppressScrollX: true }">
      @for (item of displayedData; track item; let index = $index) {
        <div [ngbTooltip]="itemHoverTooltip?.displayCondition(item) ? itemHoverTooltip.templateTooltip : undefined">
          <app-dropdown-option
            [style.height]="buttonHeight + 'px'"
            [disabled]="getIsValueDisabled(item)"
            [selected]="item === value"
            (click)="selectItem(item)"
          >
            <ng-container
              *ngTemplateOutlet="customDropdownOptionRef || customOption; context: { thisItem: item, index }"
            ></ng-container>
          </app-dropdown-option>
        </div>
      }

      @if (displayEmptyState && !displayedData?.length) {
        <span class="empty-list-placeholder">{{ emptyListPlaceholder | translate }}</span>
      }
    </perfect-scrollbar>

    @for (option of customOptions; track index; let index = $index) {
      <app-dropdown-option
        [attr.data-test-id]="option.id"
        [style.height.px]="buttonHeight"
        [menuAction]="option"
        (click)="close()"
      ></app-dropdown-option>
    }

    @if (isAddOptionShown) {
      <app-add-option
        [entityNameText]="entityNameText"
        [value]="searchField.value"
        [disabled]="shouldDisableAddOptionHandler ? shouldDisableAddOptionHandler(searchField.value) : false"
        (click)="addClick()"
      ></app-add-option>
    }

    @if (clearEnabled && value && !searchField.value) {
      <app-clear-option
        data-test-id="clear-option"
        [text]="clearOptionCustomText ?? 'components.dropdowns.coloredDropdownControl.clear' | translate"
        (click)="clearClick()"
      ></app-clear-option>
    }
  </app-dropdown-options-backdrop>
</ng-template>

<ng-template #customOption let-item="thisItem" let-index="index">
  <div class="flex gap-x-2.5 overflow-hidden">
    @if (item.icon) {
      <svg-icon [src]="item.icon"></svg-icon>
    }

    @if (numerateOptions) {
      <span>({{ index + 1 }})</span>
    }

    <span
      class="flex-1 truncate"
      [ngbTooltip]="getDisplayValue(item)"
      [openTooltipWhenOverflowedAndHovered]="true"
      [openOnWidthOverflow]="true"
      [placement]="'top'"
    >
      {{ getDisplayValue(item) }}
    </span>
  </div>
</ng-template>

<ng-template #defaultTooltip>
  <span>{{ getDisplayValue(value) }}</span>
</ng-template>
